import React, { useState } from "react";
import { Box, Typography, IconButton, Menu, MenuItem } from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

function ChatBubble({ text, fromMe, timestamp, status, onDelete }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const formatTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);

    // Check for invalid date
    if (isNaN(date.getTime())) return "";

    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    const timePart = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    if (isToday) {
      // ✅ This ensures your own messages sent right now show "Today"
      return `Today, ${timePart}`;
    }

    const datePart = date.toLocaleDateString([], {
      month: "short",
      day: "numeric",
    });

    return `${datePart}, ${timePart}`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "sending":
        // Spinner or distinct icon could go here if desired
        return <DoneIcon sx={{ fontSize: 14, color: "#ccc", opacity: 0.5 }} />;
      case "sent":
        return <DoneIcon sx={{ fontSize: 14, color: "#999" }} />;
      case "delivered":
        return <DoneAllIcon sx={{ fontSize: 14, color: "#999" }} />;
      case "read":
        return <DoneAllIcon sx={{ fontSize: 14, color: "#4CAF50" }} />;
      default:
        return null;
    }
  };

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleDelete = () => {
    handleMenuClose();
    onDelete?.();
  };

  return (
    <Box
      display="flex"
      justifyContent={fromMe ? "flex-end" : "flex-start"}
      mb={2}
    >
      <Box
        sx={{
          position: "relative",
          bgcolor: fromMe ? "#DCF8C6" : "#EAEAEA",
          px: 2,
          py: 1,
          borderRadius: "15px",
          maxWidth: "75%",
          boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
          display: "inline-flex",
          flexDirection: "column",
        }}
      >
        {/* Message Content + Dropdown */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Typography
            variant="body1"
            sx={{
              wordBreak: "break-word",
              pr: 1, // Space for the arrow
            }}
          >
            {text}
          </Typography>
          <IconButton
            size="small"
            sx={{
              p: 0,
              mt: -0.5,
              opacity: 0.5,
              "&:hover": { opacity: 1 },
            }}
            onClick={handleMenuOpen}
          >
            <ArrowDropDownIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Info Row: Time and Status */}
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          mt={0.3}
          sx={{ minWidth: "fit-content", alignSelf: "flex-end" }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "rgba(0, 0, 0, 0.45)",
              fontSize: "0.65rem",
              whiteSpace: "nowrap",
              mr: 0.5,
            }}
          >
            {formatTime(timestamp)}
          </Typography>
          {fromMe && getStatusIcon(status)}
        </Box>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          disableScrollLock
        >
          <MenuItem
            onClick={handleDelete}
            sx={{ color: "error.main", fontSize: "0.9rem" }}
          >
            Delete Message
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}

export default ChatBubble;
